import { lazy } from 'react';
import {
    OnloadFn,
    OnloadFnExperiencePage,
    OnloadFnPackagePage,
    TeCommerce,
    IRouterPage
} from './interfaces/interfaces';

// COMPONENTS
import { handlePackagesRequest, resetPackage, setDidClickFetchPackages } from './helpers/shared';
import { getCarVerification } from './helpers/vehicle';
import ecommerce from './helpers/ecommerce';
import { IUserContextProps } from './context/UserContext';
import contentBlocks from './assets/json/content-blocks.json';

// CAR
const Cpr = lazy(() => import('./pages/Cpr'));
const Vehicle = lazy(() => import('./pages/CAR/Vehicle'));
const VehicleType = lazy(() => import('./pages/CAR/VehicleType'));
const Experience = lazy(() => import('./pages/CAR/Experience'));
const Claims = lazy(() => import('./pages/CAR/Claims'));

// CHILD
const NumberOfChildren = lazy(() => import('./pages/CHILD/NumberOfChildren'));
const CriticalIllnessCoverage = lazy(() => import('./pages/CHILD/CriticalIllnessCoverage'));

// COMMON
const PackagePage = lazy(() => import('./pages/PackagePage'));
const Customer = lazy(() => import('./pages/Customer'));
const Identify = lazy(() => import('./pages/Identify'));
const Receipt = lazy(() => import('./pages/Receipt'));
const ShoppingCart = lazy(() => import('./pages/ShoppingCart'));
const Finalize = lazy(() => import('./pages/Finalize'));
const PackageLoader = lazy(() => import('./pages/PackageLoader'));
const MileageHelperModal = lazy(() => import('./pages/modals/MileageHelperModal'));
const AdditionalCoverages = lazy(() => import('./pages/AdditionalCoverages'));
const LogonToContinue = lazy(() => import('./pages/LogonToContinue'));
const Confirm = lazy(() => import('./pages/Confirm'));
const CoverageModal = lazy(() => import('./pages/modals/CoverageModal'));
const PaymentModal = lazy(() => import('./pages/modals/PaymentModal'));
const EmptyShoppingCartModal = lazy(() => import('./pages/modals/EmptyShoppingCartModal'));
const TermsModal = lazy(() => import('./pages/modals/TermsModal'));
const OtherNeedsCoverage = lazy(() => import('./pages/OtherNeedsCoverage'));

const CAR: IRouterPage[] = [
    {
        key: 'vehicle',
        order: 10,
        path: '/',
        flow: 'ALL',
        boxTitle: 'Beregn hurtigt en pris på din bilforsikring',
        size: 'MEDIUM',
        requiresLogin: false,
        hasNavigation: true,
        hasGiftcard: true,
        validateFields: ['registrationNumber'],
        component: Vehicle,
        progressBarStep: 1
    },
    {
        key: 'vehicletype',
        order: 15,
        path: '/leasingbil',
        flow: 'ALL',
        boxTitle: 'Leasing',
        size: 'MEDIUM',
        requiresLogin: false,
        hasNavigation: false,
        hasGiftcard: true,
        validateFields: [],
        component: VehicleType,
        progressBarStep: 2
    },
    {
        key: 'customer',
        order: 17,
        path: '/kunde-eller-ej',
        flow: 'NONCUSTOMER',
        size: 'MEDIUM',
        boxTitle: 'Er du allerede kunde i Topdanmark?',
        initFieldsOnLoad: ['isCustomer'],
        requiresLogin: false,
        hasNavigation: false,
        hasGiftcard: true,
        validateFields: [],
        component: Customer,
        progressBarStep: 3
    },

    {
        key: 'identify',
        order: 18,
        path: '/om-dig',
        flow: 'NONCUSTOMER',
        size: 'MEDIUM',
        boxTitle: 'Fortæl os, hvem du er',
        initFieldsOnLoad: ['cprMitId'],
        requiresLogin: false,
        hasNavigation: false,
        hasGiftcard: true,
        validateFields: [],
        onLoadFn: ({ config, dispatch }: Pick<OnloadFn, 'config' | 'dispatch'>) => {
            resetPackage({ config, dispatch });
            setDidClickFetchPackages({ dispatch, didClick: false });
        },
        component: Identify,
        progressBarStep: 3
    },
    {
        key: 'cpr',
        order: 20,
        path: '/cpr',
        flow: 'NONCUSTOMER',
        size: 'MEDIUM',
        boxTitle: 'Navn, adresse og CPR-nummer',
        hasNavigation: true,
        hasGiftcard: true,
        requiresLogin: false,
        validateFields: ['fullName', 'cprNo', 'address'],
        onLoadFn: ({ config, dispatch }: Pick<OnloadFn, 'config' | 'dispatch'>) => {
            resetPackage({ config, dispatch });
            setDidClickFetchPackages({ dispatch, didClick: false });
        },
        component: Cpr,
        progressBarStep: 3
    },
    {
        key: 'experience',
        order: 22,
        path: '/erfaring',
        flow: 'ALL',
        boxTitle: 'Hvor mange år har du haft bilforsikring?',
        size: 'MEDIUM',
        hasNavigation: true,
        hasGiftcard: true,
        requiresLogin: false,
        validateFields: ['ownCarYears'],
        onLoadFn: ({ config, dispatch, selection, user, logGetCarVerificationError }: OnloadFnExperiencePage) => {
            getCarVerification({
                config,
                dispatch,
                selection,
                user,
                logGetCarVerificationError
            } as Required<OnloadFnExperiencePage> & {
                user: IUserContextProps;
            });
        },
        component: Experience,
        progressBarStep: 4
    },
    {
        key: 'claims',
        order: 24,
        path: '/antal-skader',
        flow: 'ALL',
        boxTitle: 'Tidligere skader',
        size: 'MEDIUM',
        hasNavigation: true,
        hasGiftcard: true,
        requiresLogin: false,
        validateFields: ['numberOfClaims', 'latestClaimYear'],
        initFieldsOnLoad: ['isCustomer'],
        onLoadFn: ({ config, dispatch }: Pick<OnloadFn, 'config' | 'dispatch'>) => {
            resetPackage({ config, dispatch });
            setDidClickFetchPackages({ dispatch, didClick: false });
        },
        component: Claims,
        progressBarStep: 5
    },
    {
        key: 'package-loader',
        order: 59,
        path: '/hent-pakker',
        flow: 'ALL',
        size: 'LARGE',
        hasNavigation: false,
        requiresLogin: false,
        hideProgressbar: true,
        isLoaderPage: true,
        validateFields: [],
        onLoadFn: ({ config, dispatch, selection, model, user, logHandlePackagesRequestError }: OnloadFnPackagePage) =>
            handlePackagesRequest({
                config,
                dispatch,
                logHandlePackagesRequestError,
                selection,
                model,
                user
            } as Required<OnloadFnPackagePage>),
        component: PackageLoader
    },
    {
        key: 'package',
        order: 60,
        path: '/pakker',
        flow: 'ALL',
        size: 'LARGE',
        title: 'Vælg pakken, der matcher dig',
        titleStyle: { display: 'flex', margin: 'auto', justifyContent: 'center' },
        hasNavigation: false,
        hasGiftcard: true,
        requiresLogin: false,
        hideProgressbar: true,
        children: [
            {
                path: '/pakker/daekning/:coverageid',
                element: <CoverageModal />
            },
            {
                path: '/pakker/koerselsbehov',
                element: <MileageHelperModal />
            }
        ],
        validateFields: [],
        onLoadFn: ({ dispatch }: Pick<OnloadFn, 'dispatch'>) => setDidClickFetchPackages({ dispatch, didClick: false }),
        component: PackagePage
    },
    {
        key: 'additional-coverages',
        order: 62,
        path: '/tilvalg',
        flow: 'ALL',
        size: 'LARGE',
        title: 'Gør forsikringen endnu bedre',
        validateFields: [],
        hideProgressbar: true,
        hasNavigation: true,
        requiresLogin: false,
        continueFn: (params: TeCommerce) => {
            ecommerce.addToCart({ ...params });
        },
        children: [
            {
                path: '/tilvalg/andre-daekningsbehov',
                element: <OtherNeedsCoverage />
            }
        ],
        component: AdditionalCoverages
    },
    {
        key: 'summary',
        order: 64,
        path: '/indkoebskurv',
        flow: 'ALL',
        title: 'Indkøbskurv',
        altTitle: contentBlocks.noContent3738,
        validateFields: [],
        hideProgressbar: true,
        hasNavigation: true,
        requiresLogin: false,
        primaryBtnText: 'Gå til betaling',
        altPrimaryBtnText: 'Ring mig op',
        altConditions: 'checkForFullCustomer3738',
        continueFn: (params: TeCommerce) => ecommerce.checkout({ ...params }),
        component: ShoppingCart,
        children: [
            {
                path: '/indkoebskurv/fjern-indhold',
                element: <EmptyShoppingCartModal product="CAR" />
            },
            {
                path: '/indkoebskurv/hvad-er-inkluderet',
                element: <TermsModal product="CAR" />
            }
        ]
    },
    {
        key: 'confirm',
        order: 72,
        path: '/afslut',
        flow: 'ALL',
        size: 'LARGE',
        title: 'Færdiggør købet',
        hasNavigation: false,
        requiresLogin: false,
        hideProgressbar: true,
        validateFields: [
            'firstName',
            'lastName',
            'address',
            'mobilePhone',
            'emailAddress',
            'alreadyCovered',
            'selectedDueDate',
            'effectiveDate',
            'currentInsuranceCompany',
            'insuranceStartDate',
            'complyCorrectData',
            'complyDisclaimers',
            'complyConsent',
            'complyStrictTerms',
            'complyRki',
            'complyNonCommercial'
        ],
        component: Confirm
    },
    {
        key: 'login',
        order: 74,
        path: '/log-ind',
        flow: 'NONCUSTOMER',
        size: 'SMALL',
        hasNavigation: false,
        hideProgressbar: true,
        requiresLogin: false,
        validateFields: [],
        component: LogonToContinue
    },
    {
        key: 'finalize',
        order: 85,
        path: '/gennemfoer',
        flow: 'ALL',
        size: 'LARGE',
        hasNavigation: false,
        hideProgressbar: true,
        requiresLogin: true,
        isLoaderPage: true,
        validateFields: [],
        component: Finalize
    },
    {
        key: 'receipt',
        order: 90,
        path: '/kvittering',
        flow: 'ALL',
        size: 'LARGE',
        hasNavigation: false,
        hideProgressbar: true,
        requiresLogin: false,
        validateFields: [],
        continueFn: (params: TeCommerce) => ecommerce.purchase({ ...params }),
        component: Receipt
    }
];

const CHILD: IRouterPage[] = [
    {
        key: 'numberOfChildren',
        order: 10,
        path: '/',
        flow: 'ALL',
        requiresLogin: false,
        boxTitle: 'Hvor mange børn skal forsikres?',
        validateFields: ['numberOfChildren'],
        hasNavigation: true,
        continueFn: () => {},
        component: NumberOfChildren,
        progressBarStep: 1,
        hasGiftcard: true
    },
    {
        key: 'customer',
        order: 22,
        path: '/kunde-eller-ej',
        flow: 'NONCUSTOMER',
        size: 'MEDIUM',
        boxTitle: 'Er du allerede kunde i Topdanmark?',
        initFieldsOnLoad: ['isCustomer'],
        requiresLogin: false,
        hasNavigation: false,
        hasGiftcard: true,
        validateFields: [],
        onLoadFn: ({ config, dispatch }: Pick<OnloadFn, 'config' | 'dispatch'>) => {
            resetPackage({ config, dispatch });
            setDidClickFetchPackages({ dispatch, didClick: false });
        },
        component: Customer,
        progressBarStep: 2
    },
    {
        key: 'identify',
        order: 26,
        path: '/om-dig',
        flow: 'NONCUSTOMER',
        size: 'MEDIUM',
        boxTitle: 'Fortæl os, hvem du er',
        initFieldsOnLoad: ['cprMitId'],
        requiresLogin: false,
        hasNavigation: false,
        hasGiftcard: true,
        validateFields: [],
        onLoadFn: ({ config, dispatch }: Pick<OnloadFn, 'config' | 'dispatch'>) => {
            resetPackage({ config, dispatch });
            setDidClickFetchPackages({ dispatch, didClick: false });
        },
        component: Identify,
        progressBarStep: 2
    },
    {
        key: 'cpr',
        order: 30,
        path: '/cpr',
        flow: 'NONCUSTOMER',
        boxTitle: 'Navn, adresse og CPR-nummer',
        size: 'MEDIUM',
        hasNavigation: true,
        hasGiftcard: true,
        requiresLogin: false,
        validateFields: ['fullName', 'cprNo', 'address'],
        component: Cpr,
        progressBarStep: 2
    },
    {
        key: 'criticalIllnessCoverage',
        order: 41,
        path: '/daekninger',
        flow: 'ALL',
        size: 'MEDIUM',
        boxTitle: 'I hvilke situationer skal dine børn være dækket?',
        requiresLogin: false,
        hasGiftcard: true,
        validateFields: ['criticalIllnessCoverage'],
        component: CriticalIllnessCoverage,
        progressBarStep: 3
    },
    {
        key: 'package-loader',
        order: 59,
        path: '/hent-pakker',
        flow: 'ALL',
        size: 'LARGE',
        hasNavigation: false,
        requiresLogin: false,
        isLoaderPage: true,
        hideProgressbar: true,
        validateFields: [],
        onLoadFn: ({ config, dispatch, selection, model, user, logHandlePackagesRequestError }: OnloadFnPackagePage) =>
            handlePackagesRequest({
                config,
                dispatch,
                logHandlePackagesRequestError,
                selection,
                model,
                user
            } as Required<OnloadFnPackagePage>),
        component: PackageLoader
    },
    {
        key: 'package',
        order: 60,
        path: '/pakker',
        flow: 'ALL',
        hideProgressbar: true,
        hasNavigation: false,
        validateFields: [],
        size: 'LARGE',
        requiresLogin: false,
        title: 'Vælg pakken, der matcher dig',
        hasGiftcard: true,
        titleStyle: { display: 'flex', margin: 'auto', justifyContent: 'center' },
        onLoadFn: ({ dispatch }: Pick<OnloadFn, 'dispatch'>) => setDidClickFetchPackages({ dispatch, didClick: false }),
        component: PackagePage,
        children: [
            {
                path: '/pakker/daekning/:coverageid',
                element: <CoverageModal />
            },
            {
                path: '/pakker/udbetaling',
                element: <PaymentModal />
            }
        ]
    },
    {
        key: 'healthinsurance',
        order: 61,
        path: '/tilvalg',
        flow: 'ALL',
        size: 'LARGE',
        title: 'Gør forsikringen endnu bedre',
        validateFields: [],
        hideProgressbar: true,
        hasNavigation: true,
        requiresLogin: false,
        continueFn: (params: TeCommerce) => {
            ecommerce.addToCart({ ...params });
        },
        component: AdditionalCoverages
    },
    {
        key: 'shoppingcart',
        order: 70,
        path: '/indkoebskurv',
        flow: 'ALL',
        hideProgressbar: true,
        hasNavigation: true,
        validateFields: [],
        primaryBtnText: 'Gå til betaling',
        requiresLogin: false,
        continueFn: (params: TeCommerce) => ecommerce.checkout({ ...params }),
        component: ShoppingCart,
        children: [
            {
                path: '/indkoebskurv/fjern-indhold',
                element: <EmptyShoppingCartModal product="CHILD" />
            },
            {
                path: '/indkoebskurv/hvad-er-inkluderet',
                element: <TermsModal product="CHILD" />
            }
        ]
    },

    {
        key: 'confirm',
        order: 71,
        path: '/afslut',
        flow: 'ALL',
        size: 'LARGE',
        title: 'Færdiggør købet',
        hasNavigation: false,
        requiresLogin: false,
        hideProgressbar: true,
        validateFields: [
            'firstName',
            'lastName',
            'address',
            'mobilePhone',
            'emailAddress',
            'alreadyCovered',
            'selectedDueDate',
            'effectiveDate',
            'currentInsuranceCompany',
            'insuranceStartDate',
            'complyCorrectData',
            'complyDisclaimers',
            'complyConsent',
            'complyRki',
            'complyNonCommercial',
            'bankRegNumber',
            'bankAccountNumber'
        ],
        component: Confirm
    },
    {
        key: 'login',
        order: 74,
        path: '/log-ind',
        flow: 'NONCUSTOMER',
        size: 'SMALL',
        hasNavigation: false,
        hideProgressbar: true,
        requiresLogin: false,
        validateFields: [],
        component: LogonToContinue
    },
    {
        key: 'finalize',
        order: 85,
        path: '/gennemfoer',
        flow: 'ALL',
        size: 'LARGE',
        hasNavigation: false,
        hideProgressbar: true,
        requiresLogin: true,
        isLoaderPage: true,
        validateFields: [],
        component: Finalize
    },
    {
        key: 'receipt',
        order: 90,
        path: '/kvittering',
        flow: 'ALL',
        size: 'LARGE',
        hasNavigation: false,
        hideProgressbar: true,
        requiresLogin: false,
        validateFields: [],
        continueFn: (params: TeCommerce) => ecommerce.purchase({ ...params }),
        component: Receipt
    }
];

export const pages = {
    CAR,
    CHILD
};
